<i18n>
{
  "fr": {
    "tool-title": "Profil piézométrique",
    "HEADS-label": "Niveau [msm]",
    "HEADS-unit": "msm",
    "EZNS-label": "Profondeur [m]",
    "EZNS-unit": "m",
    "no-data-title": "Aucune donnée",
    "HEADS": "Niveau piézométrique",
    "EZNS": "Épaisseur de la zone non saturée",
    "HEADS_REF": "Valeurs de référence (1976-2017)",
    "EZNS_REF": "Valeurs de référence (1976-2017)",
    "MNT_VSC": "Topographie",
    "catalog-title": "Catalogue mensuel",
    "resolution": "Résolution",
    "distance-to-origin-label": "Distance à l'origine [m]"
  },
  "de": {

  }
}
</i18n>

<template>
  <div id="gw-heads-raster-profil" class="tool" v-if="show">
    <h3>{{$t('tool-title')}}</h3>
    <md-content class="md-scrollbar main-container" v-if="!loading">
      <md-empty-state
        v-if="nodata"
        class="empty-data"
        md-rounded
        md-icon="graphic_eq"
        :md-label="$t('no-data-title')"
        :md-size="170"
      >
      </md-empty-state>
      <div class="graph-option">
        <md-field class="option">
          <md-select v-model="selectedParameterType" :disabled="datasourceLoading">
            <md-option value="HEADS">{{ $t('HEADS') }}</md-option>
            <md-option value="EZNS">{{ $t('EZNS') }}</md-option>
          </md-select>
        </md-field>
        <md-field class="option">
          <label>{{ $t('resolution') }}</label>
          <md-select v-model="currentResolution" :disabled="datasourceLoading">
            <md-option v-for="resolution in availableResolution" :value="resolution" :key="resolution">{{ resolution }} m</md-option>
          </md-select>
        </md-field>
      </div>
      <div class="data-graph" v-if="!nodata">
        <plotly
          v-if="showGraph"
          :data="graphData"
          :layout="graphLayout"
          @mouse-over="onMouseHover"
          @mouse-out="onMouseOut"
          ref="plotly"
        />
      </div>
      <div class="datasources" v-if="!nodata">
        <div :class="['datasource', 'reference', {'disabled': !datasource.show}]" v-for="datasource in referenceDatasourcesToDisplay" :key="datasource.code" @click="toggleDatasource(datasource)">
          <div class="datasource-loading" v-if="datasource.loading">
            <md-progress-spinner :md-diameter="10" :md-stroke="2" md-mode="indeterminate"></md-progress-spinner>
          </div>
          <div class="datasource-linecolor" v-if="!datasource.loading" :style="{'background-color': datasource.color}"></div>
          <div class="datasource-title">{{ $t(datasource.code) }}</div>
        </div>
        <div :class="['catalog', 'monthly']" v-for="catalog in catalogDatasourcesToDisplay" :key="catalog.code">
          <div class="catalog-title">{{ $t('catalog-title') }}</div>
          <div class="datasource-year-container">
            <div :class="['datasource', 'year', {'disabled': !datasource.show || datasource.nodata}]" v-for="datasource in catalog.datasources" :key="datasource.code" @click="toggleDatasource(datasource)">
              <div class="datasource-loading" v-if="datasource.loading">
                <md-progress-spinner :md-diameter="10" :md-stroke="2" md-mode="indeterminate"></md-progress-spinner>
              </div>
              <div class="datasource-linecolor" v-if="datasource.show && !datasource.loading && !datasource.nodata" :style="{'background-color': datasource.color}"></div>
              <div class="datasource-title">{{ datasource.year }}</div>
            </div>
          </div>
        </div>
      </div>
    </md-content>
    <div class="loading" v-if="loading">
      <md-progress-spinner class="loading-animation" :md-diameter="40" :md-stroke="5" md-mode="indeterminate"></md-progress-spinner>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import _ from 'lodash'

import TimeStoreMixin from '@/mixins/store/time'
import MapStoreMixin from '@/mixins/store/map'
import CommonStoreMixin from '@/mixins/store/common'

import GeomToolbox from '@/utils/geomToolbox'
import SourceProxy from '@/proxies/source'
import GwHeadsRefLayer from '@/components/rasters/gwHeadsRefLayer'
import Layer from '@/components/vue-map/layers/layer'
import plotly from '@/components/common/plotly'
import { PlotlyData, PlotlyXAxis, PlotlyYAxis, PlotlyLayout } from '@/utils/plotly'

import { Fill, Stroke, Style, Circle } from 'ol/style'
import { Vector as VectorSource } from 'ol/source'
import { Vector as VectorLayer } from 'ol/layer'
import GeoJSON from '@/utils/geoJson'
import { get as getProjection } from 'ol/proj'
import { Feature } from 'ol'

import Config from '@/config.js'

const NODATA_VALUES = [-3.4028231e+38, -9999, 9999]

const samplingPointStyle = new Style({
  image: new Circle({
    radius: 3,
    fill: new Fill({
      color: 'rgba(255, 255, 255, 0.7)'
    }),
    stroke: new Stroke({
      color: '#184aa0'
    }),
  })
})

const samplingPointHoverStyle = new Style({
  image: new Circle({
    radius: 5,
    fill: new Fill({
      color: 'rgba(255, 255, 255, 1)'
    }),
    stroke: new Stroke({
      color: '#184aa0'
    }),
  })
})

export default {
  name: 'GwHeadsRasterProfil',
  components: {
    plotly
  },
  mixins: [
    TimeStoreMixin,
    MapStoreMixin,
    CommonStoreMixin
  ],
  props: {
    params: {
      type: Object,
      default: () => { return {} }
    }
  },
  computed: {
    show () {
      return this.currentGeom !== null && this.currentGeom.type === 'LineString'
    },
    nodata () {
      return false
    },
    datasourceLoading () {
      return this.datasources.filter((datasource) => datasource.loading).length > 0
    },
    showGraph () {
      return !this.loading && !_.isEmpty(this.graphData) && !this.nodata
    },
    graphData () {
      let toReturn = []
      let graphData
      this.datasources.forEach((datasource) => {
        graphData = this.getGraphData(datasource, this.dateMiddle)
        if (graphData !== null && datasource.show) toReturn.push(graphData)
      })
      return toReturn
    },
    graphLayout () {
      let toReturn = new PlotlyLayout()
      toReturn.xaxis = new PlotlyXAxis(this.$t('distance-to-origin-label'))
      toReturn.yaxis = new PlotlyYAxis(this.$t(`${this.selectedParameterType}-label`))
      toReturn.yaxis.range = this.calculateRangeFromReference()
      toReturn.yaxis.fixedrange = true
      toReturn.yaxis.mode = 'linear'
      toReturn.yaxis.dtick= 0.5
      toReturn.margin = {t: 30, b: 40, r: 30}
      toReturn.displayHoverLine()

      return toReturn
    },
    availableResolution () {
      if (this.lineLength > 10000) return [100, 1000, 2000, 5000, 10000]
      if (this.lineLength > 1000 && this.lineLength <= 10000) return [10, 20, 50, 100, 200, 500, 1000]
      return [10, 20, 50, 100]
    },
    defaultResolution () {
      if (this.lineLength > 10000) return 2000
      if (this.lineLength > 1000 && this.lineLength <= 10000) return 200
      if (this.lineLength > 100 && this.lineLength <= 1000) return 50
      return 20
    },
    samplingPoints () {
      if (this.currentGeom === null || this.currentGeom.type !== 'LineString') return []
      let samplingPoints = GeomToolbox.samplePointsOnLine(this.currentGeom, this.currentResolution)
      let samplingPointGeom

      samplingPoints.forEach((samplingPoint, index) => {
        samplingPoint.distanceFromOrigin = index * this.currentResolution

        samplingPointGeom = GeoJSON.readGeometry(samplingPoint, {
          dataProjection: getProjection(Config.mapConfiguration.projection),
          featureProjection: getProjection(Config.mapConfiguration.projection)
        })
        samplingPoint.feature = new Feature(samplingPointGeom)
        samplingPoint.feature.setStyle(samplingPointStyle)
      })

      return samplingPoints
    },
    currentReferenceDatasource () {
      return this.datasources.find((datasource) => datasource.code.includes(this.selectedParameterType) && datasource.code.includes('REF'))
    },
    topographyDatasource () {
      return this.datasources.find((datasource) => datasource.code.includes('MNT'))
    },
    referenceDatasourcesToDisplay () {
      return this.datasources.filter((datasource) => datasource.code.includes(this.selectedParameterType) && datasource.code.includes('REF') || datasource.code.includes('MNT'))
    },
    catalogDatasourcesToDisplay () {
      // L'affichage doit représenter les sources de données temporelles en catalogue d'année à sélectionner
      let catalogDatasources = this.datasources.filter((datasource) => datasource.code.includes(this.selectedParameterType) && datasource.code.includes('CATALOG'))
      let catalogs = _.groupBy(catalogDatasources, (datasource) => { return datasource.code.split('_')[0] })
      let toReturn = []
      for (const [key, value] of Object.entries(catalogs))  {
        toReturn.push({
          code: key,
          name: value[0].code,
          datasources: _.orderBy(value, ['year'], ['asc'])
        })
      }
      return toReturn
    },
    loadCatalog () {
      return !this.params.fetchRefOnly
    }
  },
  watch: {
    currentGeom () {
      this.updateTool()
    },
    samplingPoints () {
      if (this.samplingPoints.length === 0) return
      this.hoverLayer.getSource().clear()
      this.samplingPoints.forEach((samplingPoint, index) => {
        if (index === 0) return
        this.hoverLayer.getSource().addFeature(samplingPoint.feature)
      })

      this.mapRef.getView().fit(this.hoverLayer.getSource().getExtent(), {
        padding: [200, (window.innerWidth / 2) + 200, 200, 200],
        duration: 500,
        maxZoom: 23
      })
    },
    dateMiddle () {
      this.refreshGraph()
      if (this.dateMiddle.year() !== this.selectedYear) {
        this.selectedYear = this.dateMiddle.year()
      }
    },
    selectedYear () {
      if (!this.loadCatalog) return
      // On éteind toutes les couches catalogues allumées qui ne sont pas de l'année sélectionnée
      this.datasources.filter((datasource) => datasource.code.includes(`${this.selectedParameterType}_CATALOG`) && !datasource.code.includes(this.selectedYear)).forEach((datasource) => this.disableDatasource(datasource))
      // On allume les couches catalogues qui sont de l'année sélectionnée
      this.datasources.filter((datasource) => datasource.code.includes(`${this.selectedParameterType}_CATALOG`) && datasource.code.includes(this.selectedYear)).forEach((datasource) => this.enableDatasource(datasource))
    },
    currentResolution () {
      if (!this.isInit) return
      this.refreshDatasources()
    },
    selectedParameterType () {
      if (!this.isInit) return
      // On éteind toutes les couches allumées
      this.datasources.filter((datasource) => datasource.show).forEach((datasource) => this.disableDatasource(datasource))
      // On allume la couche de référence
      this.enableDatasource(this.currentReferenceDatasource)
      // On allume les couches catalogues qui sont de l'année sélectionnée
      if (this.loadCatalog) this.datasources.filter((datasource) => datasource.code.includes(`${this.selectedParameterType}_CATALOG`) && datasource.code.includes(this.selectedYear)).forEach((datasource) => this.enableDatasource(datasource))
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.updateTool()
    })
  },
  beforeDestroy() {
    this.removeTool()
    this.clearHoverFeature()
  },
  data() {
    return {
      loading: true,
      isInit: false,
      selectedParameterType: 'HEADS',
      rasters: [],
      datasources: [],
      lineLength: 0,
      currentResolution: 20,
      hoverLayer: null,
      highlightedSamplingPoint: null,
      selectedYear: moment.utc().year()
    }
  },
  methods: {
    updateTool () {
      if (this.currentGeom !== null && this.currentGeom.type === 'LineString') this.load()
      else this.removeTool()
    },
    removeTool () {
      if (this.currentGeom !== null && this.currentGeom.type === 'LineString') this.clearHoverFeature()
      this.setWideRightPanelState(false)
      if (this.hoverLayer !== null) {
        this.hoverLayer.getSource().clear()
        this.mapRef.removeLayer(this.hoverLayer)
        this.hoverLayer = null
      }
    },
    load () {
      this.loading = true
      this.isInit = false
      this.setWideRightPanelState(true)
      this.setLeftPanelState(false)
      this.lineLength = GeomToolbox.getLength(this.currentGeom)
      this.currentResolution = this.defaultResolution
      this.initHoverLayer()
      this.fetchRasters(this.params.sourcesToFetch).then(() => {
        this.generateDatasources()
        this.isInit = true
        this.loading = false
        // Chargement de la source de donnée de référence
        this.enableDatasource(this.currentReferenceDatasource)
        // Chargement de la source de donnée catalogue
        if (this.loadCatalog) this.datasources.filter((datasource) => datasource.code.includes(`${this.selectedParameterType}_CATALOG`) && datasource.code.includes(this.selectedYear)).forEach((datasource) => this.enableDatasource(datasource))
      }).catch(this.handleError)
    },
    // Récupération des sources de données rasters et création de leur objet respectif
    fetchRasters (sourceIds) {
      return new Promise((resolve, reject) => {
        this.rasters = []
        SourceProxy.getSources(sourceIds).then((sources) => {
          sources.forEach((source) => {
            if (source) {
              if (source.geoserverConfig.sourceName.includes('REF')) {
                this.rasters.push(new GwHeadsRefLayer(source))
              } else {
                this.rasters.push(Layer.fromSource(source))
              }
            }
          })
          resolve()
        }).catch(reject)
      })
    },
    generateDatasources () {
      let toolParams = null

      this.datasources = []
      let tmpDatasource

      // Topographie
      let topographyRaster = this.rasters.find((raster) => raster.geoserverConfig.sourceName?.includes('MNT'))
      if (topographyRaster) {
        toolParams = topographyRaster.webviewConfig.availableTools.find((tool) => tool.key === 'GW_HEADS_RASTER_PROFIL').params
        tmpDatasource = {
          code: topographyRaster.geoserverConfig.sourceName,
          raster: topographyRaster,
          show: false,
          loading: false,
          geoms: [],
          plotlyData: null,
          nodata: false,
          color: toolParams?.color,
          fetch: null,
          isEmpty: (datasource) => { return datasource.geoms.length === 0 }
        }
        tmpDatasource.fetch = this.fetchTopography.bind(this, tmpDatasource)
        this.datasources.push(tmpDatasource)
      }

      // Sources de référence et catalogue
      this.rasters.filter((raster) => raster.geoserverConfig.sourceName?.includes('REF') || raster.geoserverConfig.sourceName?.includes('CATALOG')).forEach((raster) => {
        toolParams = raster.webviewConfig.availableTools.find((tool) => tool.key === 'GW_HEADS_RASTER_PROFIL').params
        tmpDatasource = {
          code: raster.geoserverConfig.sourceName,
          raster: raster,
          show: false,
          loading: false,
          data: [],
          nodata: false,
          color: toolParams?.color,
          fetch: null,
          isEmpty: (datasource) => { return datasource.data.length === 0 }
        }
        tmpDatasource.fetch = this.fetchDatasource.bind(this, tmpDatasource)
        if (raster.geoserverConfig.sourceName.includes('CATALOG')) {
          // Assignation de l'année du catalogue sur la datasource
          tmpDatasource.year = tmpDatasource.code.split('_').pop()
          // Mise à jour de l'année représentant le premier catalogue de la liste
          this.selectedYear = this.selectedYear > tmpDatasource.year ? tmpDatasource.year : this.selectedYear
        }
        this.datasources.push(tmpDatasource)
      })
    },
    initHoverLayer () {
      if (this.hoverLayer !== null) return this.hoverLayer.getSource().clear()

      let hoverSource = new VectorSource({
        features: []
      })

      this.hoverLayer = new VectorLayer({
        source: hoverSource,
        zIndex: 200
      })

      this.mapRef.addLayer(this.hoverLayer)
    },
    compute () {
      return new Promise((resolve) => {
        resolve()
      })
    },
    fetchDatasource (datasource) {
      return new Promise((resolve, reject) => {
        if (datasource.data.length === 0) {
          // La source n'a jamais été chargée, on l'initialise
          for (var i = 1; i <= 12; i++) datasource.data.push({
            month: i.toString(),
            geoms: [],
            plotlyData: null
          })
        }

        let currentMonth = this.dateMiddle.format('M')
        let currentMonthDatasource = datasource.data.find((d) => { return d.month === currentMonth })
        // On trie les points à charger de ceux existant
        let geomsToFetch = _.differenceWith(this.samplingPoints, currentMonthDatasource.geoms, (a, b) => {
          return a.coordinates[0] === b.coordinates[0] && a.coordinates[1] === b.coordinates[1]
        })

        let toFetch = []
        let params = []
        geomsToFetch.forEach((geom) => {
          params = []
          params['INFO_FORMAT'] = 'json'
          toFetch.push(datasource.raster.getFromGeom(this.mapRef, geom, params))
        })

        Promise.all(toFetch).then((result) => {
          let currentGeom

          result.forEach((monthlyData, index) => {
            // A chaque requête, on reçoit du service tous les mois. il faut propager toutes les données
            currentGeom = geomsToFetch[index]
            monthlyData.forEach((data) => {
              currentMonthDatasource = datasource.data.find((d) => { return d.month === data.month })
              currentMonthDatasource.geoms.push({
                coordinates: currentGeom.coordinates,
                distanceFromOrigin: currentGeom.distanceFromOrigin,
                value: data.value
              })
              currentMonthDatasource.plotlyData = this.convertToGraphData(datasource, currentMonthDatasource.geoms)
              currentMonthDatasource.geoms = _.orderBy(currentMonthDatasource.geoms, ['distanceFromOrigin'], ['asc'])
            })
          })

          resolve(datasource)
        }).catch(reject)
      })
    },
    fetchTopography (datasource) {
      return new Promise((resolve, reject) => {
        let geomsToFetch = []
        geomsToFetch = _.differenceWith(this.samplingPoints, datasource.geoms, (a, b) => {
          return a.coordinates[0] === b.coordinates[0] && a.coordinates[1] === b.coordinates[1]
        })

        let toFetch = []
        let params = []
        geomsToFetch.forEach((geom) => {
          params = []
          params['INFO_FORMAT'] = 'json'
          toFetch.push(datasource.raster.getFromGeom(this.mapRef, geom, params))
        })

        let currentGeom
        Promise.all(toFetch).then((result) => {
          result.forEach((data, index) => {
            currentGeom = geomsToFetch[index]
            datasource.geoms.push({
              coordinates: currentGeom.coordinates,
              distanceFromOrigin: currentGeom.distanceFromOrigin,
              value: data.elevation
            })
          })
          datasource.geoms = _.orderBy(datasource.geoms, ['distanceFromOrigin'], ['asc'])
          datasource.plotlyData = this.convertToGraphData(datasource, datasource.geoms)
          resolve(datasource)
        }).catch(reject)
      })
    },
    getGraphData (datasource, date) {
      if (datasource.loading) return null

      // Source de donnée de référence ou d'un catalogue
      if (datasource.code.includes('REF') || datasource.code.includes('CATALOG')) {
        let data = datasource.data.find((d) => { return d.month === date.format('M') })
        if (typeof data === 'undefined') return null
        let toReturn = _.clone(data.plotlyData)
        // Les valeurs disponibles peuvent avoir une résolution plus faible que celle demandée (car déjà chargée.) On va donc filtrer ces données
        let toRemove = []
        toReturn.x.forEach((x, index) => { if (x % this.currentResolution !== 0) toRemove.push(index) })
        toReturn.x = toReturn.x.filter((x, index) => !toRemove.includes(index))
        toReturn.y = toReturn.y.filter((x, index) => !toRemove.includes(index))

        return toReturn
      }

      // Topographie
      if (datasource.code.includes('MNT')) {
        if (this.selectedParameterType === 'EZNS' && datasource.plotlyData !== null) {
          let toReturn = _.clone(datasource.plotlyData)
          toReturn.y = toReturn.y.map(() => 0)
          return toReturn
        }
        return datasource.plotlyData
      }
    },
    convertToGraphData (datasource, data) {
      datasource.nodata = data.filter((d) => NODATA_VALUES.includes(d.value)).length === data.length
      let toReturn = new PlotlyData('', data.map((d) => d.distanceFromOrigin), data.map((d) => this.dealRasterValue(datasource, d.value)), null, datasource.color)
      let unit = this.$t(`${this.selectedParameterType}-unit`)
      toReturn.hovertemplate = `%{y:.2f} <extra>[${unit}]</extra>`
      toReturn.showlegend = false
      toReturn.line.width = 2

      return toReturn
    },
    dealRasterValue (datasource, value) {
      if (NODATA_VALUES.includes(value)) return null
      if (this.selectedParameterType === 'EZNS' && !datasource.code.includes('MNT')) return value * -1
      return value
    },
    toggleDatasource (datasource) {
      if (datasource.show) return this.disableDatasource(datasource)
      this.enableDatasource(datasource)
    },
    enableDatasource (datasource) {
      // Si les données ont déjà été chargé, on change le state
      if (!datasource.isEmpty(datasource)) {
        datasource.show = true
        this.refreshGraph()
        return
      }

      // Les données n'ont pas encore été chargées
      datasource.loading = true
      datasource.fetch().then(() => {
        datasource.loading = false
        datasource.show = true

        // Mise à jour des datasources pour la réactivité de vue
        this.$set(this.datasources, this.datasources.indexOf(datasource), datasource)
        this.refreshGraph()
      })
    },
    refreshDatasources () {
      this.datasources.filter((datasource) => datasource.show).forEach((datasource) => {
        datasource.loading = true
        datasource.fetch().then((ds) => {
          ds.loading = false
          this.refreshGraph()
        })
      })
    },
    disableDatasource (datasource) {
        datasource.show = false
        this.refreshGraph()
        return
    },
    calculateRangeFromReference () {
      // Calcule des bornes du graph sur l'axe y:
      // Prendre la source de donnée de référence affiché et faire +- un delta configuré dans les paramètres de la source sur la moyenne des données
      let referenceDatasource = this.currentReferenceDatasource
      if (typeof referenceDatasource === 'undefined') return []
      let referenceRaster = referenceDatasource.raster
      if (referenceDatasource.data.length === 0) return []

      let toolParams = referenceRaster.webviewConfig.availableTools.find((tool) => tool.key === 'GW_HEADS_RASTER_PROFIL').params
      // Mise en commun de toutes les données disponible pour en faire la moyenne
      let allData = []
      referenceDatasource.data.forEach((data) => {
        allData = _.union(allData, data.plotlyData.y)
      })
      // let meanReference = _.mean(allData)

      let min = _.min(allData) - toolParams.deltaMeanRange
      let max = _.max(allData) + toolParams.deltaMeanRange

      // Si la topo est affichée, on l'utilise la valeur max comme borne supérieur du graph
      if (this.topographyDatasource?.show) {
        toolParams = this.topographyDatasource.raster.webviewConfig.availableTools.find((tool) => tool.key === 'GW_HEADS_RASTER_PROFIL').params
        max = this.selectedParameterType === 'HEADS' ? _.max(this.topographyDatasource.plotlyData.y) : 0
      }

      return [min, max]
    },
    refreshGraph () {
      this.$nextTick(() => {
        if (typeof this.$refs.plotly !== 'undefined') {
          this.$refs.plotly.react()
          this.$refs.plotly.relayout()
        }
      })
    },
    onMouseHover (event) {
      if (this.highlightedSamplingPoint !== null) this.highlightedSamplingPoint.setStyle(samplingPointStyle)
      this.highlightedSamplingPoint = this.samplingPoints[event.pointIndex].feature
      this.highlightedSamplingPoint.setStyle(samplingPointHoverStyle)
    },
    onMouseOut () {
      this.highlightedSamplingPoint.setStyle(samplingPointStyle)
      this.highlightedSamplingPoint = null
    },
    handleError (e) {
      console.log(e)
    }
  }
}
</script>

<style lang="scss">
  #gw-heads-raster-profil {
    .empty-data {
      margin-top: 15px;
      margin-bottom: 15px;

      .md-icon {
        width: 80px;
        min-width: 80px;
        font-size: 80px !important;
      }
      .md-empty-state-label {
        font-size: 18px;
        line-height: 18px;
      }

      &.md-empty-state-leave-active {
        display: none !important;
      }
    }

    .graph-option {
      display: flex;
      justify-content: space-evenly;
      .option {
        max-width: 350px;
      }

      .md-field {
        margin: 0px;
        width: 250px;
      }
    }

    .data-graph {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 320px;

      & .plotly {
        height: 300px;
        width: 100%;
      }
    }

    .datasources {
      padding-left: 15px;
      padding-right: 15px;
    }

    .datasource {
      display: flex;
      align-items: center;

      .datasource-loading {
        margin-right: 15px;
      }

      &.disabled {
        opacity: 0.5;
      }

      .datasource-title {
        cursor: pointer;
        user-select: none;
        padding-right: 5px;
      }

      .datasource-linecolor {
        width: 15px;
        height: 2px;
        margin-right: 10px;
      }
    }

    .catalog {
      &.monthly {
        display: flex;

        .catalog-title {
          display: flex;
          align-items: center;
        }
      }

      .datasource-year-container {
        display: flex;
        align-items: center;
        justify-content: center;
        margin-left: 15px;
      }

      .catalog-title {
        margin-left: 25px;
      }

      .datasource.year {
        height: 30px;
        min-width: 55px;
        display: flex;
        justify-content: center;
        align-items: center;
        border: 1px solid #d4d4d4;
        margin: 5px;
        cursor: pointer;
        margin-right: 5px;

        .datasource-loading {
          margin-right: 5px;
          margin-left: 10px;
        }

        &.disabled .datasource-title{
          margin-left: 5px;
        }

        .datasource-loading {
          margin-left: 10px;
        }

        .datasource-linecolor {
          margin-left: 5px;
        }
      }
    }

  }
</style>
